<template>
  <div class="modal fade show d-block" tabindex="-1" style="background-color: rgba(0,0,0,0.5)">
    <div class="modal-dialog modal-lg">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">Process Claim - {{ claim.claim_number }}</h5>
          <button type="button" class="btn-close" @click="$emit('close')"></button>
        </div>

        <div class="modal-body">
          <!-- Member & Claim Summary -->
          <div class="card border mb-4">
            <div class="card-body">
              <div class="row">
                <div class="col-md-6">
                  <h6 class="text-primary mb-3">Member Information</h6>
                  <p class="mb-1"><strong>Name:</strong> {{ claim.first_name }} {{ claim.surname }}</p>
                  <p class="mb-1"><strong>CBHI Number:</strong> {{ claim.cbhi_number }}</p>
                  <p class="mb-1"><strong>Household Size:</strong> {{ claim.household_size }} members</p>
                  <p class="mb-0">
                    <strong>Status:</strong>
                    <span v-if="claim.member_active" class="badge bg-success">Active</span>
                    <span v-else class="badge bg-danger">Inactive</span>
                  </p>
                </div>
                <div class="col-md-6">
                  <h6 class="text-primary mb-3">Claim Details</h6>
                  <p class="mb-1"><strong>Service Date:</strong> {{ formatDate(claim.service_date) }}</p>
                  <p class="mb-1"><strong>Hospital:</strong> {{ claim.hospital_name }}</p>
                  <p class="mb-1"><strong>Type:</strong> {{ claim.type_name }}</p>
                  <p class="mb-0"><strong>Diagnosis:</strong> {{ claim.diagnosis || '-' }}</p>
                </div>
              </div>
            </div>
          </div>

          <!-- Financial Summary -->
          <div class="card border mb-4">
            <div class="card-header bg-light">
              <h6 class="mb-0"><i class="bx bx-calculator me-1"></i>Financial Analysis</h6>
            </div>
            <div class="card-body">
              <div class="row mb-3">
                <div class="col-md-4">
                  <div class="text-center">
                    <h4 class="mb-1">ETB {{ formatAmount(claim.annual_limit || 0) }}</h4>
                    <p class="text-muted mb-0">Annual Limit</p>
                  </div>
                </div>
                <div class="col-md-4">
                  <div class="text-center">
                    <h4 class="mb-1 text-warning">ETB {{ formatAmount(claim.year_consumed_amount || 0) }}</h4>
                    <p class="text-muted mb-0">Already Consumed</p>
                  </div>
                </div>
                <div class="col-md-4">
                  <div class="text-center">
                    <h4 class="mb-1 text-success">ETB {{ formatAmount(claim.remaining_balance || 0) }}</h4>
                    <p class="text-muted mb-0">Available Balance</p>
                  </div>
                </div>
              </div>

              <!-- Progress Bar -->
              <div class="progress mb-3" style="height: 20px;">
                <div class="progress-bar" role="progressbar"
                     :style="{width: utilizationPercentage + '%'}"
                     :class="getUtilizationClass()">
                  {{ utilizationPercentage.toFixed(1) }}% Used
                </div>
              </div>

              <!-- Claim Amount Analysis -->
              <div class="card bg-light">
                <div class="card-body">
                  <div class="row align-items-center">
                    <div class="col-md-6">
                      <h5 class="mb-2">Claimed Amount</h5>
                      <h3 class="text-primary mb-0">ETB {{ formatAmount(claim.total_amount) }}</h3>
                    </div>
                    <div class="col-md-6">
                      <div v-if="claim.total_amount <= (claim.remaining_balance || 0)" class="alert alert-success mb-0">
                        <i class="bx bx-check-circle me-2"></i>
                        Full amount can be approved within balance
                      </div>
                      <div v-else class="alert alert-warning mb-0">
                        <i class="bx bx-info-circle me-2"></i>
                        Claimed amount exceeds available balance
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Action Selection -->
          <div class="card border">
            <div class="card-header bg-light">
              <h6 class="mb-0"><i class="bx bx-check-square me-1"></i>Processing Decision</h6>
            </div>
            <div class="card-body">
              <div class="form-check mb-3">
                <input class="form-check-input" type="radio" name="action" id="approveAction"
                       v-model="action" value="approve" :disabled="!claim.member_active">
                <label class="form-check-label" for="approveAction">
                  <strong class="text-success">Approve Claim</strong>
                  <span v-if="!claim.member_active" class="text-danger ms-2">(Member is inactive)</span>
                </label>
              </div>

              <!-- Approval Details -->
              <div v-if="action === 'approve'" class="ms-4 mb-3">
                <div class="row">
                  <div class="col-md-6">
                    <label class="form-label">Approved Amount <span class="text-danger">*</span></label>
                    <div class="input-group">
                      <span class="input-group-text">ETB</span>
                      <input type="number" class="form-control" v-model.number="approvedAmount"
                             :max="maxApprovalAmount" min="0" step="0.01" required>
                    </div>
                    <small class="text-muted">Max: ETB {{ formatAmount(maxApprovalAmount) }}</small>
                  </div>
                  <div class="col-md-6">
                    <label class="form-label">Patient Contribution</label>
                    <div class="input-group">
                      <span class="input-group-text">ETB</span>
                      <input type="text" class="form-control" :value="formatAmount(patientContribution)" readonly>
                    </div>
                  </div>
                </div>

                <!-- Approval Reason -->
                <div class="row mt-3">
                  <div class="col-12">
                    <label class="form-label">Approval Reason <span class="text-danger">*</span></label>
                    <select class="form-select mb-2" v-model="approvalReasonCode" required>
                      <option value="">Select approval reason...</option>
                      <option v-for="reason in approvalReasons" :key="reason.reason_code" :value="reason.reason_code">
                        {{ reason.reason_name }}
                      </option>
                    </select>
                    <textarea class="form-control" rows="2" v-model="approvalReasonText"
                              placeholder="Additional notes or justification (optional)..."></textarea>
                  </div>
                </div>
              </div>

              <div class="form-check">
                <input class="form-check-input" type="radio" name="action" id="rejectAction"
                       v-model="action" value="reject">
                <label class="form-check-label" for="rejectAction">
                  <strong class="text-danger">Reject Claim</strong>
                </label>
              </div>

              <!-- Rejection Reason -->
              <div v-if="action === 'reject'" class="ms-4 mt-3">
                <label class="form-label">Rejection Reason <span class="text-danger">*</span></label>
                <select class="form-select mb-2" v-model="rejectionReasonCode" required>
                  <option value="">Select a reason...</option>
                  <option v-for="reason in rejectionReasons" :key="reason.reason_code" :value="reason.reason_code">
                    {{ reason.reason_name }}
                  </option>
                </select>
                <textarea class="form-control" rows="3" v-model="rejectionReasonText"
                          placeholder="Provide additional details..." required></textarea>
              </div>

              <!-- Processing Notes -->
              <div class="mt-3">
                <label class="form-label">Processing Notes</label>
                <textarea class="form-control" rows="2" v-model="processingNotes"
                          placeholder="Any additional notes about this claim processing..."></textarea>
              </div>
            </div>
          </div>

          <!-- Warning/Info Messages -->
          <div v-if="!claim.member_active" class="alert alert-danger mt-3">
            <i class="bx bx-error-circle me-2"></i>
            <strong>Warning:</strong> This member's status is inactive. Claims cannot be approved for inactive members.
          </div>

          <div v-if="claim.days_pending > 7" class="alert alert-warning mt-3">
            <i class="bx bx-time-five me-2"></i>
            This claim has been pending for {{ claim.days_pending }} days and requires urgent processing.
          </div>

          <!-- Validation Errors -->
          <div v-if="validationErrors.length > 0" class="alert alert-danger mt-3">
            <i class="bx bx-error-circle me-2"></i>
            <strong>Please fix the following errors:</strong>
            <ul class="mb-0 mt-2">
              <li v-for="error in validationErrors" :key="error">{{ error }}</li>
            </ul>
          </div>
        </div>

        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" @click="$emit('close')">
            <i class="bx bx-x me-1"></i>Cancel
          </button>
          <button type="button" class="btn btn-primary" @click="processClaim"
                  :disabled="!canProcess || processing">
            <span v-if="processing" class="spinner-border spinner-border-sm me-2" role="status"></span>
            <i v-else class="bx bx-check me-1"></i>
            {{ processing ? 'Processing...' : (action === 'approve' ? 'Approve' : 'Reject') }} Claim
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "ClaimProcessModal",
  props: {
    claim: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      action: '',
      approvedAmount: 0,
      approvalReasonCode: '',
      approvalReasonText: '',
      rejectionReasonCode: '',
      rejectionReasonText: '',
      processingNotes: '',
      processing: false,
      validationErrors: [],
      approvalReasons: [
        { reason_code: 'FULL_COVERAGE', reason_name: 'Full Coverage Approved' },
        { reason_code: 'PARTIAL_COVERAGE', reason_name: 'Partial Coverage Approved' },
        { reason_code: 'EMERGENCY_CARE', reason_name: 'Emergency Care Approved' },
        { reason_code: 'PREVENTIVE_CARE', reason_name: 'Preventive Care Approved' },
        { reason_code: 'CHRONIC_CONDITION', reason_name: 'Chronic Condition Management' },
        { reason_code: 'MATERNITY_CARE', reason_name: 'Maternity Care Approved' },
        { reason_code: 'PEDIATRIC_CARE', reason_name: 'Pediatric Care Approved' },
        { reason_code: 'EXCEPTIONAL_CASE', reason_name: 'Exceptional Case Approved' }
      ],
      rejectionReasons: [
        { reason_code: 'INVALID_DIAGNOSIS', reason_name: 'Invalid/Incomplete Diagnosis' },
        { reason_code: 'SERVICE_NOT_COVERED', reason_name: 'Service Not Covered' },
        { reason_code: 'EXCEEDED_LIMIT', reason_name: 'Coverage Limit Exceeded' },
        { reason_code: 'MISSING_DOCUMENTS', reason_name: 'Missing Required Documents' },
        { reason_code: 'DUPLICATE_CLAIM', reason_name: 'Duplicate Claim' },
        { reason_code: 'MEMBER_INACTIVE', reason_name: 'Member Inactive' },
        { reason_code: 'EXPIRED_COVERAGE', reason_name: 'Coverage Expired' },
        { reason_code: 'INVALID_PROVIDER', reason_name: 'Invalid Healthcare Provider' },
        { reason_code: 'FRAUDULENT_CLAIM', reason_name: 'Fraudulent Activity Suspected' },
        { reason_code: 'INCOMPLETE_INFORMATION', reason_name: 'Incomplete Claim Information' }
      ]
    };
  },
  computed: {
    maxApprovalAmount() {
      return Math.min(
          parseFloat(this.claim.total_amount || 0),
          parseFloat(this.claim.remaining_balance || 0)
      );
    },

    patientContribution() {
      return Math.max(0, parseFloat(this.claim.total_amount || 0) - parseFloat(this.approvedAmount || 0));
    },

    utilizationPercentage() {
      if (!this.claim.annual_limit || parseFloat(this.claim.annual_limit) === 0) return 0;
      const consumed = parseFloat(this.claim.year_consumed_amount || 0);
      const limit = parseFloat(this.claim.annual_limit);
      return Math.min(100, (consumed / limit) * 100);
    },

    canProcess() {
      if (!this.action || this.processing) return false;

      this.validateForm();
      return this.validationErrors.length === 0;
    }
  },

  mounted() {
    // Set default approved amount to maximum allowable
    this.approvedAmount = this.maxApprovalAmount;
  },

  methods: {
    validateForm() {
      this.validationErrors = [];

      if (this.action === 'approve') {
        if (!this.claim.member_active) {
          this.validationErrors.push('Cannot approve claims for inactive members');
        }

        if (!this.approvedAmount || parseFloat(this.approvedAmount) <= 0) {
          this.validationErrors.push('Approved amount is required and must be greater than 0');
        }

        if (parseFloat(this.approvedAmount) > this.maxApprovalAmount) {
          this.validationErrors.push(`Approved amount cannot exceed ETB ${this.formatAmount(this.maxApprovalAmount)}`);
        }

        if (!this.approvalReasonCode) {
          this.validationErrors.push('Approval reason is required');
        }
      }

      if (this.action === 'reject') {
        if (!this.rejectionReasonCode) {
          this.validationErrors.push('Rejection reason is required');
        }

        if (!this.rejectionReasonText || this.rejectionReasonText.trim().length < 10) {
          this.validationErrors.push('Detailed rejection explanation is required (minimum 10 characters)');
        }
      }
    },

    async processClaim() {
      if (!this.canProcess) return;

      this.processing = true;

      try {
        if (this.action === 'approve') {
          const approvalReason = this.getFullApprovalReason();
          await this.$emit('approve', this.claim.claim_id, this.approvedAmount, approvalReason, this.processingNotes);
        } else if (this.action === 'reject') {
          const rejectionReason = this.getFullRejectionReason();
          await this.$emit('reject', this.claim.claim_id, rejectionReason, this.processingNotes);
        }
      } catch (error) {
        console.error('Error processing claim:', error);
      } finally {
        this.processing = false;
      }
    },

    getFullApprovalReason() {
      const reasonName = this.approvalReasons.find(r => r.reason_code === this.approvalReasonCode)?.reason_name || '';
      let fullReason = `${this.approvalReasonCode}: ${reasonName}`;

      if (this.approvalReasonText && this.approvalReasonText.trim()) {
        fullReason += ` - ${this.approvalReasonText.trim()}`;
      }

      return fullReason;
    },

    getFullRejectionReason() {
      const reasonName = this.rejectionReasons.find(r => r.reason_code === this.rejectionReasonCode)?.reason_name || '';
      return `${this.rejectionReasonCode}: ${reasonName} - ${this.rejectionReasonText}`;
    },

    formatDate(dateString) {
      if (!dateString) return '-';
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    },

    formatAmount(amount) {
      return new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(parseFloat(amount) || 0);
    },

    getUtilizationClass() {
      if (this.utilizationPercentage >= 90) return 'bg-danger';
      if (this.utilizationPercentage >= 70) return 'bg-warning';
      return 'bg-success';
    }
  }
};
</script>

<style scoped>
.modal.show {
  display: block !important;
}

.form-check-input {
  cursor: pointer;
}

.form-check-label {
  cursor: pointer;
}

.progress {
  background-color: #e9ecef;
}

.spinner-border-sm {
  width: 1rem;
  height: 1rem;
}

.alert ul {
  padding-left: 1.5rem;
}

.card {
  box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
}

.card-header {
  background-color: rgba(0, 0, 0, 0.03);
  border-bottom: 1px solid rgba(0, 0, 0, 0.125);
}

.badge {
  font-size: 0.75em;
}

.input-group-text {
  background-color: #e9ecef;
  border-color: #ced4da;
}

.form-control:focus, .form-select:focus {
  border-color: #80bdff;
  box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
}

.btn:disabled {
  opacity: 0.65;
  cursor: not-allowed;
}

.text-primary {
  color: #0d6efd !important;
}

.text-success {
  color: #198754 !important;
}

.text-danger {
  color: #dc3545 !important;
}

.text-warning {
  color: #ffc107 !important;
}

.text-muted {
  color: #6c757d !important;
}

.bg-light {
  background-color: #f8f9fa !important;
}

.border {
  border: 1px solid #dee2e6 !important;
}

.mb-0 { margin-bottom: 0 !important; }
.mb-1 { margin-bottom: 0.25rem !important; }
.mb-2 { margin-bottom: 0.5rem !important; }
.mb-3 { margin-bottom: 1rem !important; }
.mb-4 { margin-bottom: 1.5rem !important; }

.mt-2 { margin-top: 0.5rem !important; }
.mt-3 { margin-top: 1rem !important; }

.ms-1 { margin-left: 0.25rem !important; }
.ms-2 { margin-left: 0.5rem !important; }
.ms-4 { margin-left: 1.5rem !important; }

.me-1 { margin-right: 0.25rem !important; }
.me-2 { margin-right: 0.5rem !important; }

.fw-bold { font-weight: bold !important; }
</style>